import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageTitle from './PageTitle';
import Spacer from './Spacer';
import { getParticipantIdFromSearchParams } from '../utils/urlParams';
import './DemographicsPage.styles.css';

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || '').trim();

const LIKERT_VALUES = [1, 2, 3, 4, 5] as const;

const RISK_ITEMS = [
  {
    key: 'riskFun',
    text: 'Taking risks makes life more fun',
  },
  {
    key: 'friendsRiskTaker',
    text: "My friends would say that I'm a risk taker",
  },
  {
    key: 'enjoyRisks',
    text: 'I enjoy taking risks in most aspects of my life',
  },
  {
    key: 'riskDespiteHurt',
    text: 'I would take a risk even if it meant I might get hurt',
  },
  {
    key: 'riskImportant',
    text: 'Taking risks is an important part of my life',
  },
  {
    key: 'riskyDecisions',
    text: 'I commonly make risky decisions',
  },
  {
    key: 'takingChances',
    text: 'I am a believer of taking chances',
  },
  {
    key: 'attractedByRisk',
    text: 'I am attracted, rather than scared, by risk',
  },
] as const;

type RiskKey = (typeof RISK_ITEMS)[number]['key'];
type RiskAnswers = Partial<Record<RiskKey, number>>;

const PostGameRiskPage: React.FC = () => {
  const [answers, setAnswers] = useState<RiskAnswers>({});
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const queryParams = new URLSearchParams(window.location.search);
  const participantId = getParticipantIdFromSearchParams(window.location.search);

  const handleSelect = (key: RiskKey, value: number) => {
    setAnswers((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!participantId) {
      setError(
        'Participant ID missing from URL. Please use your study link (e.g. ?participantId=... or ?prolificID=...).'
      );
      return;
    }

    const missing = RISK_ITEMS.find((item) => answers[item.key] == null);
    if (missing) {
      setError('Please answer all questions before continuing.');
      return;
    }

    setSubmitting(true);
    try {
      const body: Record<string, number> = {};
      for (const item of RISK_ITEMS) {
        body[item.key] = answers[item.key] as number;
      }

      const res = await fetch(`${API_BASE_URL}/postGameRisk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'prolific-id': participantId,
        },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        const errMsg =
          typeof data.error === 'string' ? data.error : 'Failed to submit questionnaire.';
        setError(errMsg);
        setSubmitting(false);
        return;
      }

      setSubmitted(true);
      setTimeout(() => {
        navigate('/demographics?' + queryParams.toString());
      }, 1000);
    } catch {
      setError('Network error. Please try again.');
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="demographics-bg">
        <div className="demographics-card fade-in postgame-reflection-card">
          <div className="demographics-thankyou">
            Thank you! Redirecting to demographic questions...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="demographics-bg">
      <div className="demographics-card fade-in postgame-reflection-card">
        <PageTitle title="Risk Attitudes" />
        <Spacer />

        <p className="postgame-risk-intro">
          Please indicate how much you agree with each statement.
          <br />
          <span className="postgame-risk-intro-scale">
            1 = Strongly Disagree &nbsp;·&nbsp; 5 = Strongly Agree
          </span>
        </p>

        <form onSubmit={handleSubmit} className="demographics-form">
          {error && <div className="demographics-error">{error}</div>}

          {RISK_ITEMS.map((item, index) => (
            <div className="demographics-field postgame-risk-item" key={item.key}>
              <fieldset style={{ border: 'none', margin: 0, padding: 0 }}>
                <legend
                  className="demographics-label"
                  style={{ marginBottom: 8, display: 'block', width: '100%' }}
                >
                  {index + 1}. {item.text}{' '}
                  <span className="required" style={{ display: 'inline' }}>
                    *
                  </span>
                </legend>
                <div className="postgame-likert-scale-wrap">
                  <div className="postgame-likert-scale-labels">
                    <span>Strongly Disagree</span>
                    <span>Strongly Agree</span>
                  </div>
                  <div
                    className="postgame-likert-scale"
                    role="radiogroup"
                    aria-label={`${item.text} rating from 1 to 5`}
                  >
                    {LIKERT_VALUES.map((value) => (
                      <button
                        key={value}
                        type="button"
                        className={`postgame-likert-option${
                          answers[item.key] === value ? ' selected' : ''
                        }`}
                        onClick={() => handleSelect(item.key, value)}
                        disabled={submitting}
                        aria-pressed={answers[item.key] === value}
                        aria-label={`${value} of 5`}
                      >
                        {value}
                      </button>
                    ))}
                  </div>
                </div>
              </fieldset>
            </div>
          ))}

          <button type="submit" className="demographics-submit" disabled={submitting}>
            {submitting ? 'Submitting...' : 'Continue to Demographics'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default PostGameRiskPage;
